import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type AdminCosts } from "@/lib/api";
import { formatCost, formatCostLong, formatTokens, relativeIso } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useNow } from "@/hooks/useNow";

// ── Types ────────────────────────────────────────────────────────

type InfraService = AdminCosts["infraServices"][number];
type AiByModel = AdminCosts["aiByModel"][number];
type AiByContainer = AdminCosts["aiByContainer"][number];
type ComputeByContainer = AdminCosts["computeByContainer"][number];

// Matches the `getComputeSyncHealth` ALERT_THRESHOLD_HOURS on the backend
// so stale styling lines up with what `/api/admin/infrastructure/compute-sync-status`
// considers unhealthy.
const STALE_AFTER_HOURS = 12;

// ── Stat card ───────────────────────────────────────────────────

function StatCard({ title, value, accent }: { title: string; value: string; accent?: string }) {
  return (
    <Card className="py-4">
      <CardHeader className="pb-0">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-2xl font-semibold tabular-nums ${accent ?? ""}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card className="py-4">
      <CardHeader className="pb-0">
        <Skeleton className="h-4 w-24" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-20 mt-1" />
      </CardContent>
    </Card>
  );
}

// ── Column definitions ──────────────────────────────────────────

const aiModelColumns: ColumnDef<AiByModel>[] = [
  { accessorKey: "model", header: "Model" },
  {
    accessorKey: "cost",
    header: "Cost",
    cell: ({ getValue }) => <span className="font-medium tabular-nums">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "inputTokens",
    header: "Input Tokens",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatTokens(getValue() as number)}</span>,
  },
  {
    accessorKey: "outputTokens",
    header: "Output Tokens",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatTokens(getValue() as number)}</span>,
  },
  {
    accessorKey: "requests",
    header: "Requests",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{(getValue() as number).toLocaleString()}</span>,
  },
];

const aiContainerColumns: ColumnDef<AiByContainer>[] = [
  { accessorKey: "containerName", header: "Container" },
  {
    accessorKey: "cost",
    header: "Cost",
    cell: ({ getValue }) => <span className="font-medium tabular-nums">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "inputTokens",
    header: "Input Tokens",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatTokens(getValue() as number)}</span>,
  },
  {
    accessorKey: "outputTokens",
    header: "Output Tokens",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatTokens(getValue() as number)}</span>,
  },
  {
    accessorKey: "requests",
    header: "Requests",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{(getValue() as number).toLocaleString()}</span>,
  },
];

function formatMinutes(n: number): string {
  if (n < 60) return `${n.toFixed(1)} min`;
  if (n < 1440) return `${(n / 60).toFixed(1)} hr`;
  return `${(n / 1440).toFixed(1)} days`;
}

function formatGbMin(n: number): string {
  if (n < 60) return `${n.toFixed(1)} GB·min`;
  return `${(n / 60).toFixed(1)} GB·hr`;
}

const computeContainerColumns: ColumnDef<ComputeByContainer>[] = [
  { accessorKey: "containerName", header: "Container" },
  {
    accessorKey: "cpuMinutes",
    header: "CPU",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatMinutes(getValue() as number)}</span>,
  },
  {
    accessorKey: "memoryGbMinutes",
    header: "Memory",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatGbMin(getValue() as number)}</span>,
  },
  {
    accessorKey: "volumeGbHours",
    header: "Disk",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{(getValue() as number).toFixed(1)} GB·hr</span>,
  },
  {
    accessorKey: "backupGbHours",
    header: "Backup",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{(getValue() as number).toFixed(1)} GB·hr</span>,
  },
  {
    accessorKey: "storageCost",
    header: "Storage $",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "rawCost",
    header: "Raw Cost",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "markedUpCost",
    header: "Billed",
    cell: ({ getValue }) => <span className="font-medium tabular-nums">{formatCost(getValue() as number)}</span>,
  },
];

const infraColumns: ColumnDef<InfraService>[] = [
  { accessorKey: "serviceName", header: "Service" },
  {
    accessorKey: "serviceType",
    header: "Type",
    cell: ({ getValue }) => <Badge variant="outline">{getValue() as string}</Badge>,
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{relativeIso(getValue() as string | null)}</span>
    ),
  },
  {
    accessorKey: "cpuCost",
    header: "CPU",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "memoryCost",
    header: "Memory",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "networkCost",
    header: "Network",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "volumeCost",
    header: "Volume",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "backupCost",
    header: "Backup",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "rawCost",
    header: "Total",
    cell: ({ getValue }) => <span className="font-medium tabular-nums">{formatCost(getValue() as number)}</span>,
  },
];

type ServiceByContainer = AdminCosts["searchByContainer"][number];

// One per-container cost table per service (search, sandbox, image, tts).
const serviceContainerColumns = (header: string): ColumnDef<ServiceByContainer>[] => [
  { accessorKey: "containerName", header: "Container" },
  {
    accessorKey: "cost",
    header,
    cell: ({ getValue }) => <span className="font-medium tabular-nums">{formatCost(getValue() as number)}</span>,
  },
];

const searchContainerColumns = serviceContainerColumns("Search cost");
// Sandbox spend is synced from E2B only, so a Boo on Vercel Sandbox is
// labelled instead of shown as $0 (or as its pre-move E2B spend alone).
type SandboxCostRow = { containerId: string; containerName: string; cost: number | null; onVercel: boolean };

function sandboxCostRows(data: AdminCosts): SandboxCostRow[] {
  const vercelContainers = data.vercelSandboxContainers ?? [];
  const vercel = new Set(vercelContainers.map((c) => c.containerId));
  const rows: SandboxCostRow[] = data.sandboxByContainer.map((r) => ({ ...r, onVercel: vercel.has(r.containerId) }));
  const listed = new Set(rows.map((r) => r.containerId));
  for (const c of vercelContainers) {
    if (!listed.has(c.containerId)) rows.push({ ...c, cost: null, onVercel: true });
  }
  return rows;
}

const sandboxContainerColumns: ColumnDef<SandboxCostRow>[] = [
  { accessorKey: "containerName", header: "Container" },
  {
    accessorKey: "cost",
    header: "Sandbox cost",
    cell: ({ row }) => {
      const { cost, onVercel } = row.original;
      if (cost === null) return <span className="text-muted-foreground italic">not tracked for Vercel</span>;
      return (
        <span className="font-medium tabular-nums">
          {formatCost(cost)}
          {onVercel && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">E2B only · not tracked for Vercel</span>
          )}
        </span>
      );
    },
  },
];
const imageContainerColumns = serviceContainerColumns("Image cost");
const ttsContainerColumns = serviceContainerColumns("TTS cost");

// ── Skeleton table ──────────────────────────────────────────────

function TableSkeleton({ cols, rows = 4 }: { cols: number; rows?: number }) {
  return (
    <div className="rounded-md border">
      <div className="divide-y">
        <div className="flex gap-4 px-4 py-3">
          {Array.from({ length: cols }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-24" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex gap-4 px-4 py-3">
            {Array.from({ length: cols }).map((_, j) => (
              <Skeleton key={j} className="h-4 w-20" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Page component ──────────────────────────────────────────────

export function AdminCostsPage() {
  useDocumentTitle("Admin: Costs");
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-costs"],
    queryFn: () => api.admin.infrastructureCosts(),
  });

  // Reuses the existing compute-sync endpoint — `syncComputeUsage` on the
  // backend now also refreshes the cached Railway infra-service costs that
  // populate the Infrastructure Services table below, so one click covers
  // both. The button on the Infrastructure page is the same mutation.
  const syncCosts = useMutation({
    mutationFn: api.admin.syncCompute,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["admin-costs"] });
      queryClient.invalidateQueries({ queryKey: ["admin-compute-sync-status"] });
      toast.success(
        `Synced ${r.synced} containers (${r.errors} errors, ${r.skipped} skipped) · raw $${r.totalRawCost.toFixed(4)} · billed $${r.totalMarkedUpCost.toFixed(4)}`,
      );
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to sync costs");
    },
  });

  const now = useNow();
  const lastSyncedAt = data?.infraLastSyncedAt ?? null;
  const isStale = lastSyncedAt
    ? (now - new Date(lastSyncedAt).getTime()) / 3_600_000 > STALE_AFTER_HOURS
    : true;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title="Costs" subtitle="AI spend, compute, and infrastructure costs · last 30 days." />
        <div className="flex items-center gap-3">
          <div className="text-xs text-muted-foreground">
            Costs synced{" "}
            <span className={isStale ? "text-destructive font-medium" : ""}>
              {lastSyncedAt ? relativeIso(lastSyncedAt) : "never"}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncCosts.mutate()}
            disabled={syncCosts.isPending}
          >
            <RefreshCw className={`size-3.5 mr-1.5 ${syncCosts.isPending ? "animate-spin" : ""}`} />
            Sync now
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <StatCardSkeleton key={i} />
            ))}
          </div>
          <TableSkeleton cols={5} />
          <TableSkeleton cols={5} />
          <TableSkeleton cols={5} />
          <TableSkeleton cols={7} rows={3} />
        </div>
      ) : isError || !data ? (
        <ErrorComponent error={error ?? "Failed to load cost data"} />
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard title="AI Spend" value={formatCostLong(data.aiCost)} />
            <StatCard title="Container Compute" value={formatCostLong(data.computeCost)} />
            <StatCard title="Storage (disk + backup)" value={formatCostLong(data.storageCost)} />
            <StatCard title="Web search" value={formatCostLong(data.searchCost)} />
            <StatCard title="Sandbox (e2b)" value={formatCostLong(data.sandboxCost)} />
            <StatCard title="Images" value={formatCostLong(data.imageCost)} />
            <StatCard title="TTS" value={formatCostLong(data.ttsCost)} />
            <StatCard title="Infrastructure" value={formatCostLong(data.infraCost)} />
            <StatCard
              title="Total"
              value={formatCostLong(data.aiCost + data.computeCost + data.infraCost)}
              accent="text-primary"
            />
          </div>

          {/* BOO-266: storage trajectory. Backups accrue continuously and scale
              with customer count, so surface the month-to-date spend and a linear
              projection to month end. */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Storage MTD"
              value={formatCostLong(data.projectedStorage.monthToDate.storageCost)}
            />
            <StatCard
              title="Storage projected (month)"
              value={formatCostLong(data.projectedStorage.projectedMonth.storageCost)}
              accent="text-amber-600"
            />
            <StatCard
              title="Backups MTD"
              value={formatCostLong(data.projectedStorage.monthToDate.backupCost)}
            />
            <StatCard
              title="Backups projected (month)"
              value={formatCostLong(data.projectedStorage.projectedMonth.backupCost)}
              accent="text-amber-600"
            />
          </div>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">AI Spend by Model</h2>
              <Badge variant="secondary">{data.aiByModel.length}</Badge>
            </div>
            {data.aiByModel.length > 0 ? (
              <DataTable columns={aiModelColumns} data={data.aiByModel} />
            ) : (
              <p className="text-sm text-muted-foreground">No AI usage in this period.</p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">AI Spend by Container</h2>
              <Badge variant="secondary">{data.aiByContainer.length}</Badge>
            </div>
            {data.aiByContainer.length > 0 ? (
              <DataTable columns={aiContainerColumns} data={data.aiByContainer} />
            ) : (
              <p className="text-sm text-muted-foreground">No AI usage in this period.</p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Compute by Container</h2>
              <Badge variant="secondary">{data.computeByContainer.length}</Badge>
            </div>
            {data.computeByContainer.length > 0 ? (
              <DataTable columns={computeContainerColumns} data={data.computeByContainer} />
            ) : (
              <p className="text-sm text-muted-foreground">No compute usage in this period.</p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Web Search by Container</h2>
              <Badge variant="secondary">{data.searchByContainer.length}</Badge>
            </div>
            {data.searchByContainer.length > 0 ? (
              <DataTable columns={searchContainerColumns} data={data.searchByContainer} />
            ) : (
              <p className="text-sm text-muted-foreground">No web-search usage in this period.</p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Sandbox (e2b) by Container</h2>
              <Badge variant="secondary">{data.sandboxByContainer.length}</Badge>
              {!!data.vercelSandboxContainers?.length && (
                <span className="text-sm text-muted-foreground">
                  + {data.vercelSandboxContainers.length} on Vercel, not tracked
                </span>
              )}
            </div>
            {data.sandboxByContainer.length + (data.vercelSandboxContainers?.length ?? 0) > 0 ? (
              <DataTable columns={sandboxContainerColumns} data={sandboxCostRows(data)} />
            ) : (
              <p className="text-sm text-muted-foreground">No sandbox usage in this period.</p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Image Generation by Container</h2>
              <Badge variant="secondary">{data.imageByContainer.length}</Badge>
            </div>
            {data.imageByContainer.length > 0 ? (
              <DataTable columns={imageContainerColumns} data={data.imageByContainer} />
            ) : (
              <p className="text-sm text-muted-foreground">No image-generation usage in this period.</p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Text to Speech by Container</h2>
              <Badge variant="secondary">{data.ttsByContainer.length}</Badge>
            </div>
            {data.ttsByContainer.length > 0 ? (
              <DataTable columns={ttsContainerColumns} data={data.ttsByContainer} />
            ) : (
              <p className="text-sm text-muted-foreground">No text-to-speech usage in this period.</p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Infrastructure Services</h2>
              <Badge variant="secondary">{data.infraServices.length}</Badge>
            </div>
            {data.infraServices.length > 0 ? (
              <DataTable columns={infraColumns} data={data.infraServices} />
            ) : (
              <p className="text-sm text-muted-foreground">No infrastructure service costs in this period.</p>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
